// Notification utility for browser notifications and sound alerts

let audioContext = null;

// Initialize audio context (must be done after user interaction)
const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

// Play a gentle notification chime using Web Audio API
export const playNotificationSound = () => {
  try {
    const ctx = getAudioContext();
    
    // Resume audio context if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    const now = ctx.currentTime;
    
    // Create a gentle two-note chime
    const playNote = (frequency, startTime, duration) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      
      // Gentle envelope
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.15, startTime + 0.05); // Soft attack
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration); // Fade out
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    // Play two harmonious notes (C5 and E5) - a pleasant major third
    playNote(523.25, now, 0.3);        // C5
    playNote(659.25, now + 0.15, 0.3); // E5
    
  } catch (error) {
    console.debug('Could not play notification sound:', error);
  }
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.debug('Browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
};

// Check if notifications are enabled
export const areNotificationsEnabled = () => {
  return 'Notification' in window && Notification.permission === 'granted';
};

// Show browser notification
export const showBrowserNotification = (title, options = {}) => {
  if (!areNotificationsEnabled()) {
    return null;
  }

  try {
    const notification = new Notification(title, {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: options.tag || 'orq-notification',
      renotify: true,
      ...options,
    });

    // Auto-close after 5 seconds
    setTimeout(() => notification.close(), 5000);

    // Focus window when notification is clicked
    notification.onclick = () => {
      window.focus();
      notification.close();
      if (options.onClick) {
        options.onClick();
      }
    };

    return notification;
  } catch (error) {
    console.debug('Error showing notification:', error);
    return null;
  }
};

// Combined notification: sound + browser notification
export const notify = (title, options = {}) => {
  // Only notify if tab is not focused
  if (document.hidden) {
    playNotificationSound();
    showBrowserNotification(title, options);
  } else {
    // Still play a quieter sound even if tab is focused
    playNotificationSound();
  }
};

// Specific notification types
export const notifyChatResponse = (chatTitle) => {
  notify('Neue Antwort erhalten', {
    body: chatTitle ? `Chat: ${chatTitle}` : 'Der Agent hat geantwortet.',
    tag: 'chat-response',
  });
};

export const notifyBatchComplete = (successCount, failedCount, totalCount) => {
  const body = failedCount > 0
    ? `${successCount} erfolgreich, ${failedCount} fehlgeschlagen von ${totalCount}`
    : `Alle ${totalCount} CRs erfolgreich verarbeitet!`;
  
  notify('Batch-Verarbeitung abgeschlossen', {
    body,
    tag: 'batch-complete',
  });
};

export default {
  playNotificationSound,
  requestNotificationPermission,
  areNotificationsEnabled,
  showBrowserNotification,
  notify,
  notifyChatResponse,
  notifyBatchComplete,
};
